/**
 * AttendancePage — Jelenlét oldal
 * ClockInOutWidget + AttendanceMonthlyTable + hónap picker
 */

import { QueryErrorState } from '@/components/QueryErrorState';
import { useTranslation } from '@kgc/i18n';
import { Button } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { AttendanceMonthlyTable } from '../components/AttendanceMonthlyTable';
import { ClockInOutWidget } from '../components/ClockInOutWidget';
import { useAttendance } from '../hooks/useAttendance';
import { useEmployees } from '../hooks/useEmployees';

/** Aktuális hét hétfőjének UTC-kezdete (ISO hét, H 00:00). */
function getMondayStart(ref: Date): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const dayOfWeek = d.getDay(); // 0=Vas, 1=Hé, ..., 6=Szo
  const offsetToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  d.setDate(d.getDate() + offsetToMonday);
  return d;
}

function parseTimeOnDate(date: string, time: string | undefined): Date | null {
  if (time === undefined || time === '') return null;
  if (time.includes('T')) {
    const d = new Date(time);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [h, m, s] = time.split(':').map(x => parseInt(x, 10));
  if ([h, m].some(Number.isNaN)) return null;
  const d = new Date(date);
  d.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
  return d;
}

export function AttendancePage() {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const {
    data: records = [],
    isLoading: recordsLoading,
    isError: recordsError,
    refetch: refetchRecords,
  } = useAttendance(year, month);
  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: employeesError,
    refetch: refetchEmployees,
  } = useEmployees();
  const hasError = recordsError || employeesError;

  // Mai rekord az aktuális userhez (első közelítés: az aktuális hónapból keressük a mai napot)
  const today = now.toISOString().split('T')[0];
  const todayRecord = records.find(r => new Date(r.date).toISOString().split('T')[0] === today);

  // 106-3: heti összes munkaperc (aktuális hét H-tól most-ig)
  const weeklyMinutes = useMemo(() => {
    const monday = getMondayStart(now);
    const nowTs = now.getTime();
    let total = 0;
    for (const rec of records) {
      const recDate = new Date(rec.date);
      if (recDate < monday || recDate > now) continue;
      const start = parseTimeOnDate(rec.date, rec.actualStart);
      if (!start) continue;
      const end = parseTimeOnDate(rec.date, rec.actualEnd) ?? new Date(Math.min(nowTs, Date.now()));
      const minutes = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60_000));
      total += Math.max(0, minutes - rec.breakMinutes);
    }
    return total;
  }, [records, now]);

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  };

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
  });

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">{t('hr.attendance.title')}</h1>

      {/* ClockInOut widget */}
      <div className="max-w-sm">
        <ClockInOutWidget todayRecord={todayRecord} weeklyMinutes={weeklyMinutes} />
      </div>

      {/* Hónap picker */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={prevMonth}>
          ←
        </Button>
        <span className="text-lg font-medium min-w-[160px] text-center">{monthLabel}</span>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          →
        </Button>
      </div>

      {/* Havi jelenlét táblázat */}
      {hasError && (
        <QueryErrorState
          onRetry={() => {
            if (recordsError) void refetchRecords();
            if (employeesError) void refetchEmployees();
          }}
        />
      )}

      {!hasError &&
        (recordsLoading || employeesLoading ? (
          <p className="text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <AttendanceMonthlyTable
            year={year}
            month={month}
            employees={employees}
            records={records}
          />
        ))}
    </div>
  );
}
